// An option card's description: a short preview, with the whole text behind
// "Read more" (UnitCard). Pure: no DOM, no React, so the Playwright pure spec
// (e2e/option-description-logic.spec.ts) imports it under Node.
//
// The preview is about half of what the card used to show (its first two
// sentences), so a guest reads a line or two before "Read more" — the owner,
// 25 Sept: "I wanted to display less of the description before a guest had
// to click read more". The description itself is never changed: it stays
// exactly as the tenant wrote it, and "Read more" shows all of it.
//
// A sentence ends at . ! ? or … (and any closing quote or bracket after it)
// when what follows is whitespace and then a capital letter, or the end of
// the text. So "2.5 hours" never splits (no space), nor does "approx. 12 km"
// or "Saddle No. 4" (a number, not a capital), nor "4 riders. and" (lower
// case). "22–25 km. For experienced riders" does split: "km" is a unit, not
// an abbreviation. A sentence that starts with a number ("… upwards. 2–4
// riders.") stays with the one before it.
//
// When unsure, the text is NOT split: a preview one sentence longer is
// harmless, a preview cut after "Dr." reads as broken.

/** Words that take a full stop without ending the sentence, compared in
 *  lower case without their final stop ("e.g." → "e.g"). Only the ones a
 *  capital can follow matter: a number or a lower-case word never splits. */
const ABBREVIATIONS = new Set([
  "approx",
  "ca",
  "cf",
  "dept",
  "dr",
  "e.g",
  "eg",
  "est",
  "excl",
  "fig",
  "i.e",
  "ie",
  "incl",
  "jr",
  "mr",
  "mrs",
  "ms",
  "mt",
  "no",
  "nr",
  "prof",
  "sr",
  "st",
  "tel",
  "viz",
  "vs",
]);

// The terminator, then a lookahead for "whitespace + (opening quote or
// bracket) + capital" or for the end of the text.
const BOUNDARY = /[.!?…]+["'”’)\]]*(?=\s+["'“‘([]?\p{Lu}|\s*$)/gu;

/** Where each sentence of `text` ends (an index just past its terminator),
 *  in order. The text after the last index, if any, is one more sentence
 *  with no terminator. */
function sentenceEnds(text: string): number[] {
  const ends: number[] = [];
  for (const m of text.matchAll(BOUNDARY)) {
    const at = m.index ?? 0;
    // A lone full stop may belong to the word before it.
    if (m[0].startsWith(".") && !m[0].startsWith("..")) {
      const before = text.slice(0, at);
      const start = before.search(/\S+$/u);
      const word = start < 0 ? "" : before.slice(start).replace(/^["'“‘([]+/u, "");
      // An initial ("J. Smith") or a known abbreviation ("Dr. Smith").
      if (/^\p{L}$/u.test(word) || ABBREVIATIONS.has(word.toLowerCase())) continue;
    }
    ends.push(at + m[0].length);
  }
  return ends;
}

/** The sentences of `text`, trimmed, in order. Empty for blank text. */
export function splitSentences(text: string | null | undefined): string[] {
  const full = (text ?? "").trim();
  if (!full) return [];
  const out: string[] = [];
  let from = 0;
  for (const end of sentenceEnds(full)) {
    const s = full.slice(from, end).trim();
    if (s) out.push(s);
    from = end;
  }
  const tail = full.slice(from).trim();
  if (tail) out.push(tail);
  return out;
}

/** How many characters of a description an option card shows before
 *  "Read more": about half of the two sentences it used to show. */
export const PREVIEW_CHARS = 100;

export type DescriptionPreview = {
  /** The whole description, trimmed, exactly as written: what "Read more" opens. */
  full: string;
  /** What the card shows until "Read more" is pressed (see descriptionPreview). */
  lead: string;
  /** There is more than the lead: the card offers "Read more". */
  more: boolean;
};

/** Length in characters (code points), so "½" or an accent counts once. */
const chars = (s: string) => [...s].length;

// What a cut never ends on: spaces, commas, colons, semicolons, dashes and
// slashes, "&", full stops and ellipses (never "km.…" or ",…"), or an
// opening bracket or curly quote that has lost its words.
const DANGLING_MARKS = /[\s,:;\-‐-―\/&(\[{“‘.…]+$/u;
// A straight quote or apostrophe at the end that closes the last word
// ("the riders'", "\"Hold on.\"") is kept; one after a space, a dash or an
// opening bracket opened a quotation that has lost its words — trimmed.
const ORPHAN_QUOTE = /(^|[\s\-‐-―\/&(\[{“‘])["']+$/u;
// A comma, colon, semicolon, full stop or ellipsis INSIDE a closing quote
// or bracket at the end ("“Wow,”", "(a short ride.)") goes, and the quote
// or bracket stays with its word: never ",”…" or ".)…".
const MARKS_BEHIND_CLOSER = /[,:;.…]+(["'”’)\]}]+)$/u;
// A small word glued to an opening quote or bracket ("\"and", "(with") is
// still a small word: those are looked past when testing the last word.
const OPENERS = /^["'“‘(\[{]+/u;

/** Small words a preview never ends on before its "…": the cut backs up to
 *  the word before ("…enjoy the horses and…" reads "…enjoy the horses…"). */
export const DANGLING_WORDS: ReadonlySet<string> = new Set([
  "a",
  "an",
  "and",
  "at",
  "but",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "the",
  "to",
  "towards",
  "with",
]);

/** Abbreviations that, like a small word, only introduce what follows
 *  ("go e.g…", "incl…"), so a cut never ends on them either. (A title such
 *  as "Dr" is a word of its own: "led by Dr…" stays.) */
const DANGLING_ABBREVIATIONS: ReadonlySet<string> = new Set([
  "approx", "ca", "cf", "e.g", "eg", "excl", "i.e", "ie", "incl", "viz", "vs",
]);

/** Whether the last word of a cut dangles: a small word (DANGLING_WORDS) or
 *  an introducing abbreviation (DANGLING_ABBREVIATIONS), looked at past any
 *  opening quote or bracket glued to it ("\"and", "(e.g."). */
function danglingWord(token: string): boolean {
  const w = token.replace(OPENERS, "").replace(/[.…]+$/u, "").toLowerCase();
  return DANGLING_WORDS.has(w) || DANGLING_ABBREVIATIONS.has(w);
}

/** `kept` with whatever dangles at its end trimmed, repeatedly: marks, an
 *  orphaned opening quote, a mark behind a closing quote or bracket, and
 *  small words and introducing abbreviations (danglingWord) — but never its first word,
 *  so something is always left. */
function trimDangling(kept: string): string {
  let out = kept;
  for (;;) {
    const before = out;
    out = out.replace(DANGLING_MARKS, "");
    out = out.replace(ORPHAN_QUOTE, "$1").replace(DANGLING_MARKS, "");
    out = out.replace(MARKS_BEHIND_CLOSER, "$1");
    const m = /^([\s\S]*\S)\s+(\S+)$/u.exec(out);
    if (m && danglingWord(m[2]!)) out = m[1]!;
    if (out === before) return out;
  }
}

/** The first `budget` characters of `text`, cut at the last word boundary
 *  (whitespace) at or before them — never mid-word — with what dangles
 *  trimmed (trimDangling) and "…" added. A first word longer than the
 *  budget is kept whole. Null when nothing is left to show before the
 *  ellipsis. */
function cutAtWord(text: string, budget: number): string | null {
  // One character past the budget: a word that ends exactly at the budget
  // is followed by the whitespace this finds.
  const head = [...text].slice(0, budget + 1).join("");
  let at = head.search(/\s(?=\S*$)/u);
  if (at <= 0) at = text.search(/\s/u);
  if (at <= 0) return null;
  const kept = trimDangling(text.slice(0, at));
  return kept ? `${kept}…` : null;
}

/** What an option card shows of a description before "Read more". Null for
 *  a missing or blank description.
 *
 *  - The whole text, with no "Read more", when it is PREVIEW_CHARS
 *    characters or fewer (a one-sentence description within the budget, or
 *    two short ones: a button that opened a few more words would cost the
 *    guest a click for nothing).
 *  - Else its FIRST sentence, exactly as written, when that sentence is
 *    PREVIEW_CHARS characters or fewer.
 *  - Else the text cut at the last word boundary at or before
 *    PREVIEW_CHARS characters, never mid-word, with trailing spaces,
 *    commas, colons, semicolons, dashes and full stops trimmed (also from
 *    inside a closing quote or bracket: never ",”…" or ".)…"), backed up
 *    past a dangling small word ("and", "with", "the" … DANGLING_WORDS) or
 *    introducing abbreviation ("e.g", "incl"), even behind an opening quote ("\"and"),
 *    keeping a closing quote or apostrophe that belongs to the last word,
 *    and "…".
 *
 *  "Read more" then shows `full` in place of the lead — the whole text once,
 *  so a cut mid-sentence neither repeats nor drops a word. */
export function descriptionPreview(
  text: string | null | undefined,
  budget = PREVIEW_CHARS,
): DescriptionPreview | null {
  const full = (text ?? "").trim();
  if (!full) return null;
  if (chars(full) <= budget) return { full, lead: full, more: false };
  const first = sentenceEnds(full)[0];
  if (first != null) {
    const sentence = full.slice(0, first).trimEnd();
    if (chars(sentence) <= budget) return { full, lead: sentence, more: true };
  }
  const lead = cutAtWord(full, budget);
  return lead ? { full, lead, more: true } : { full, lead: full, more: false };
}
